import { useMemo } from "react";
import { Show } from "shared/components";
import { Application, Applicant, Verification } from "shared/types";
import { CombinedIncomeSubStep } from "../../constants";
import { IncomeStatusStep } from "./slices/IncomeStatusStep";
import { SelectIncomeTypeStep } from "./slices/SelectIncomeTypeStep";
import { CompanyDetailsStep } from "./slices/CompanyDetailsStep";
import { SupervisorDetailsStep } from "./slices/SupervisorDetailsStep";
import { PreTaxIncomeStep } from "./slices/PreTaxIncomeStep";
import { SelectDocumentTypeStep } from "./slices/SelectDocumentTypeStep";
import { DocumentUploadStep } from "./slices/DocumentUploadStep";
import { AddAnotherIncomeStep } from "./slices/AddAnotherIncomeStep";
import { NoIncomeReasonStep } from "./slices/NoIncomeReasonStep";
import { OtherIncomeDetailsStep } from "./slices/OtherIncomeDetailsStep";
import { CombinedIncomeProvider } from "./context";
import { HousingAssistanceStep } from "./slices/HousingAssistance";

interface CombinedIncomeProps {
  refetchApplication: () => void;
  applicant: Applicant;
  application: Application;
  verification?: Verification;
  onNextStep: () => void;
  refetchVerification: () => void;
}

const CombinedIncomeSteps = ({
  subStep
}: {
  subStep: CombinedIncomeSubStep;
}) => {
  return (
    <div className="h-[calc(80vh-72px)]">
      <Show if={subStep === CombinedIncomeSubStep.IncomeStatus}>
        <IncomeStatusStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.NoIncomeReason}>
        <NoIncomeReasonStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SelectIncomeType}>
        <SelectIncomeTypeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.CompanyDetails}>
        <CompanyDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.OtherIncomeDetails}>
        <OtherIncomeDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SupervisorDetails}>
        <SupervisorDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.PreTaxIncome}>
        <PreTaxIncomeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SelectDocumentType}>
        <SelectDocumentTypeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.DocumentUpload}>
        <DocumentUploadStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.AddAnotherIncome}>
        <AddAnotherIncomeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.HousingAssistance}>
        <HousingAssistanceStep />
      </Show>
    </div>
  );
};

export const CombinedIncome = ({
  refetchApplication,
  applicant,
  application,
  verification,
  onNextStep,
  refetchVerification
}: CombinedIncomeProps) => {
  const combinedIncomeSubStep = useMemo(
    () =>
      verification?.steps?.find((step) => step.name === "combined_income")?.data
        .lastStep as CombinedIncomeSubStep | undefined,
    [verification?.steps]
  );

  return (
    <CombinedIncomeProvider
      applicant={applicant}
      application={application}
      verification={verification}
      refetchApplication={refetchApplication}
      refetchVerification={refetchVerification}
      onNextStep={onNextStep}
    >
      <CombinedIncomeSteps
        subStep={combinedIncomeSubStep || CombinedIncomeSubStep.IncomeStatus}
      />
    </CombinedIncomeProvider>
  );
};
